import React, { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2, X, ChevronLeft, ChevronRight, Loader2, ClipboardCheck,
  HelpCircle, Type as TypeIcon, Wrench, Stethoscope, ShoppingCart, Lock,
} from "lucide-react";
import { toast } from "sonner";
import { flowsApi } from "../lib/api";

const ICONS = {
  questionNode: HelpCircle, inputNode: TypeIcon, actionNode: Wrench,
  diagnosisNode: Stethoscope, recommendProductNode: ShoppingCart, startNode: ClipboardCheck,
};
const KIND_LABEL = {
  questionNode: "Question", inputNode: "Input", actionNode: "Action step",
  diagnosisNode: "Diagnosis", recommendProductNode: "Recommended products", startNode: "Start",
};

// Records a node's answer in the saved-answer shape (skips Start nodes).
function answerFor(node, chosen) {
  const d = node.data || {};
  if (node.type === "questionNode") return { question: d.label || "Question", answer: chosen || "", type: "question", required: !!d.required };
  if (node.type === "inputNode") return { question: d.label || "Input", answer: chosen || "", type: "input", required: !!d.required };
  if (node.type === "actionNode") return { question: d.label || "Action", answer: d.description ? `Done — ${d.description}` : "Completed", type: "action", required: !!d.required };
  if (node.type === "diagnosisNode") return { question: d.label || "Diagnosis", answer: d.recommendation || "Noted", type: "diagnosis", required: !!d.required };
  if (node.type === "recommendProductNode") return { question: d.label || "Recommended products", answer: chosen || "Reviewed", type: "recommend", required: !!d.required };
  return null;
}

/**
 * Drives "Complete job" → confirm → step-by-step job-completion checklist(s) →
 * "Close Job". Reused by mobile + desktop. `onCompleted` should actually set the
 * job status to Completed (backend enforces all checklists are done first).
 */
export default function JobCompletionFlow({ job, onClose, onCompleted, skipConfirm = false }) {
  const [step, setStep] = useState("loading"); // loading | confirm | wizard | done
  const [flows, setFlows] = useState([]);
  const [flowIdx, setFlowIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  // per-flow navigation
  const [path, setPath] = useState([]);
  const [pos, setPos] = useState(0);
  const [answers, setAnswers] = useState({});
  const [draft, setDraft] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await flowsApi.forJob(job.id);
        if (!active) return;
        const list = (res.flows || []).filter((f) => !f.completed && (f.nodes || []).length);
        setFlows(list);
        if (skipConfirm && !list.length) {
          onCompleted();
          return;
        }
        setStep(skipConfirm ? "wizard" : "confirm");
      } catch {
        if (!active) return;
        setFlows([]);
        if (skipConfirm) onCompleted();
        else setStep("confirm");
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const flow = flows[flowIdx];
  const nodeById = useMemo(() => Object.fromEntries((flow?.nodes || []).map((n) => [n.id, n])), [flow]);

  const startId = useMemo(() => {
    if (!flow) return null;
    const start = (flow.nodes || []).find((n) => n.type === "startNode");
    return (start || flow.nodes[0])?.id || null;
  }, [flow]);

  // (Re)initialise navigation when entering the wizard or switching flows.
  useEffect(() => {
    if (step !== "wizard" || !flow) return;
    setPath(startId ? [startId] : []);
    setPos(0);
    setAnswers({});
    setDraft("");
  }, [step, flowIdx, startId, flow]);

  const node = path.length ? nodeById[path[pos]] : null;

  const outgoing = (nodeId, handle) => {
    const edges = (flow?.edges || []).filter((e) => e.source === nodeId);
    if (handle != null) {
      const m = edges.find((e) => (e.sourceHandle || "") === handle);
      return m ? m.target : null;
    }
    return edges.length ? edges[0].target : null;
  };

  const isEnd = node ? !( ((flow?.edges || []).some((e) => e.source === node.id)) ) : true;
  // Question with a chosen branch that has no edge is also an end.
  const chosenAnswer = node ? answers[node.id]?.answer : null;
  const questionDead = node?.type === "questionNode" && chosenAnswer != null && outgoing(node.id, chosenAnswer) == null;
  const atEnd = node && (isEnd || questionDead);

  const advanceTo = (nextId, record) => {
    setAnswers((a) => (record ? { ...a, [node.id]: record } : a));
    setPath((p) => {
      const base = p.slice(0, pos + 1);
      if (nextId) {
        if (base[pos + 1] === nextId) return p; // keep forward history
        return [...base, nextId];
      }
      return base;
    });
    if (nextId) setPos((i) => i + 1);
    setDraft("");
  };

  const pickChoice = (choice) => {
    const rec = answerFor(node, choice);
    const next = outgoing(node.id, choice);
    advanceTo(next, rec);
  };

  const submitInfoOrInput = () => {
    if (node.type === "inputNode") {
      const val = draft.trim();
      if ((node.data || {}).required && !val) { toast.error("This field is required."); return; }
      advanceTo(outgoing(node.id), answerFor(node, val));
    } else {
      advanceTo(outgoing(node.id), answerFor(node, null));
    }
  };

  const goBack = () => { if (pos > 0) { setPos(pos - 1); setDraft(""); } };
  const goForward = () => { if (pos < path.length - 1) { setPos(pos + 1); setDraft(""); } };

  // The answer a node already recorded — or, for the *current* node, the answer
  // it WOULD record if committed now. This lets a Required Input (or other info
  // node) that is the last node on the path satisfy the gate from its live draft,
  // since "Next" (the usual writer) is hidden at the end of a path.
  const prospectiveAnswer = (n) => {
    const recorded = answers[n.id]?.answer;
    if (recorded != null && String(recorded).trim()) return recorded;
    if (n.id === node?.id) {
      const rec = n.type === "inputNode" ? answerFor(n, draft.trim()) : answerFor(n, null);
      return rec?.answer;
    }
    return recorded;
  };

  // All Required nodes on the taken path must be answered to allow closing.
  const requiredUnmet = path.some((id) => {
    const n = nodeById[id];
    if (!n?.data?.required) return false;
    const a = prospectiveAnswer(n);
    return !a || !String(a).trim();
  });
  const canFinish = atEnd && !requiredUnmet;
  const moreFlows = flowIdx < flows.length - 1;

  const finishFlow = async () => {
    // Commit the current node's answer (e.g. a terminal Input/Action node the user
    // never pressed "Next" to leave) so it is included in the saved answers.
    const committed = { ...answers };
    if (node && node.type !== "questionNode") {
      const rec = node.type === "inputNode"
        ? answerFor(node, draft.trim() || answers[node.id]?.answer || "")
        : answerFor(node, null);
      if (rec) committed[node.id] = rec;
    }
    setBusy(true);
    try {
      const payload = {
        jobId: job.id,
        answers: path.map((id) => (committed[id] ? { ...committed[id], nodeId: id } : null)).filter(Boolean),
      };
      await flowsApi.submitAnswers(flow.id, payload);
      if (moreFlows) {
        toast.success(`"${flow.name}" saved. Next checklist…`);
        setFlowIdx((i) => i + 1);
      } else if (skipConfirm) {
        await onCompleted();
      } else {
        setStep("done");
        await onCompleted();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the checklist.");
    } finally { setBusy(false); }
  };

  const confirmYes = async () => {
    if (!flows.length) {
      setBusy(true);
      try { await onCompleted(); } finally { setBusy(false); }
      return;
    }
    setStep("wizard");
  };

  // ---------- render ----------
  const Wrap = ({ children }) => (
    <div data-testid="job-completion-flow" className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white border border-slate-200 shadow-2xl overflow-hidden">
        {children}
      </div>
    </div>
  );

  if (step === "loading") {
    return <Wrap><div className="p-10 flex justify-center"><Loader2 className="h-7 w-7 text-blue-500 animate-spin" /></div></Wrap>;
  }

  if (step === "done") {
    return (
      <Wrap>
        <div className="p-8 text-center" data-testid="job-completion-done">
          <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          </div>
          <div className="text-[17px] font-bold text-slate-900">Completing job…</div>
          <div className="text-[13px] text-slate-500 mt-1">Saving your checklist and closing the job.</div>
          <Loader2 className="h-5 w-5 text-emerald-500 animate-spin mx-auto mt-4" />
        </div>
      </Wrap>
    );
  }

  if (step === "confirm") {
    return (
      <Wrap>
        <div className="p-6 text-center">
          <div className="h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="h-6 w-6 text-blue-600" />
          </div>
          <div className="text-[17px] font-bold text-slate-900">Do you want to complete this job?</div>
          <div className="text-[13px] text-slate-500 mt-1">{job.title || job.type || "Job"}{flows.length ? ` — ${flows.length} checklist${flows.length > 1 ? "s" : ""} to finish` : ""}</div>
          <div className="flex gap-2.5 mt-5">
            <button data-testid="complete-confirm-no" onClick={onClose} disabled={busy}
              className="flex-1 h-11 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50">No</button>
            <button data-testid="complete-confirm-yes" onClick={confirmYes} disabled={busy}
              className="flex-1 h-11 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Yes"}
            </button>
          </div>
        </div>
      </Wrap>
    );
  }

  // wizard
  const Icon = ICONS[node?.type] || HelpCircle;
  const d = node?.data || {};
  const totalRequired = (flow?.nodes || []).filter((n) => n.data?.required).length;
  return (
    <Wrap>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-blue-600 flex items-center gap-1.5"><ClipboardCheck className="h-3 w-3" /> Checklist {flowIdx + 1}/{flows.length}</div>
          <div className="text-[14px] font-bold text-slate-900 truncate">{flow?.name}</div>
        </div>
        <button data-testid="wizard-close" onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-slate-200 flex items-center justify-center text-slate-500"><X className="h-4 w-4" /></button>
      </div>

      <div className="p-5 min-h-[220px]">
        {node && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-7 w-7 rounded-md bg-blue-50 flex items-center justify-center"><Icon className="h-4 w-4 text-blue-600" /></div>
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500">{KIND_LABEL[node.type]}</span>
              {d.required && <span className="text-[8.5px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200">Required</span>}
            </div>
            <div className="text-[16px] font-semibold text-slate-900 mb-3" data-testid="wizard-node-label">{d.label || KIND_LABEL[node.type]}</div>

            {node.type === "questionNode" && (
              <div className="grid gap-2" data-testid="wizard-choices">
                {(d.choices && d.choices.length ? d.choices : ["Yes", "No"]).map((c, i) => {
                  const active = answers[node.id]?.answer === c;
                  return (
                    <button key={`${c}-${i}`} data-testid={`wizard-choice-${i}`} onClick={() => pickChoice(c)}
                      className={`w-full text-left px-4 py-3 rounded-xl border text-[14px] font-medium transition-colors ${active ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-700 hover:border-blue-300 hover:bg-slate-50"}`}>
                      {c}{active && <CheckCircle2 className="h-4 w-4 inline ml-2 text-blue-600" />}
                    </button>
                  );
                })}
              </div>
            )}

            {node.type === "inputNode" && (
              <textarea
                data-testid="wizard-input"
                value={draft || answers[node.id]?.answer || ""}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={d.placeholder || "Type answer…"}
                rows={4}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[14px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 resize-none"
              />
            )}

            {(node.type === "actionNode" || node.type === "startNode") && d.description && (
              <p className="text-[13.5px] text-slate-600 leading-relaxed">{d.description}</p>
            )}
            {node.type === "diagnosisNode" && (
              <p className="text-[13.5px] text-slate-600 leading-relaxed">{d.recommendation || "Acknowledge to continue."}</p>
            )}
            {node.type === "recommendProductNode" && (
              <p className="text-[13px] text-slate-500">{(d.productIds || []).length} recommended item(s). Tap continue to proceed.</p>
            )}
          </>
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50">
        <div className="flex items-center gap-1.5">
          <button data-testid="wizard-back" onClick={goBack} disabled={pos === 0}
            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-[13px] font-medium flex items-center gap-1 disabled:opacity-40 hover:bg-white">
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          <button data-testid="wizard-forward" onClick={goForward} disabled={pos >= path.length - 1}
            className="h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-[13px] font-medium flex items-center gap-1 disabled:opacity-40 hover:bg-white">
            Forward <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {node && !atEnd && (node.type === "inputNode" || node.type === "actionNode" || node.type === "startNode" || node.type === "diagnosisNode" || node.type === "recommendProductNode") && (
          <button data-testid="wizard-next" onClick={submitInfoOrInput}
            className="h-9 px-4 rounded-lg bg-blue-600 text-white text-[13px] font-semibold flex items-center gap-1 hover:bg-blue-700">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        )}

        {canFinish && (
          <button data-testid="wizard-close-job" onClick={finishFlow} disabled={busy}
            className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-[13px] font-semibold flex items-center gap-1.5 hover:bg-emerald-700 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {moreFlows ? "Next checklist" : "Close Job"}
          </button>
        )}
        {atEnd && requiredUnmet && !canFinish && (
          <span className="text-[11px] text-rose-600 flex items-center gap-1"><Lock className="h-3 w-3" /> Answer required items</span>
        )}
      </div>
    </Wrap>
  );
}
